<template>
	<div>
		<!-- Header -->
		<div class="flex wrap xs-12 min-height mb2">
			<div class="flex xs-12 md-6 xs-left xs-middle">
				<h2 class="fs7 lh125 fw7 c1 mr4">{{ $t("proposals") }}</h2>
			</div>
			<div class="flex xs-12 md-6 xs-left xs-bottom md-right md-middle">
				<j-button @click="$modal.show('addProposal')" size="small">{{
					$t("add_proposal")
				}}</j-button>
			</div>
		</div>

		<!-- List of proposals -->
		<j-card class="mb4 bg5" v-for="proposal in proposals" :key="proposal.id">
			<div class="xs-12 flex wrap xs-middle p4">
				<div
					class="flex xs-7 xs-between wrap cu2"
					@click="$emit('proposal:clicked', proposal.id)"
				>
					<div class="xs-12">
						<!-- Name and date -->
						<div>
							<span class="c2 fw7 mr1 lh125">{{
								proposal.name || "Unnamed Proposal"
							}}</span>
							<span class="fs3 c6">({{ proposal.createdAt }})</span>
						</div>
						<!-- Proposal Number -->
						<div>
							<span class="c1 fs3 fw7 lh125"
								>{{ $t("number") }}:
								{{ proposal.proposalNumber || "---" }}</span
							>
						</div>
						<!-- Status -->
						<div>
							<span class="c1 fs3 fw7 lh125">{{ $t("status") }}: </span>
							<span
								:class="{
									'p1 r2 fw7 bgError--l5 cError--d4 fs2':
										proposal.status === 'REJECTED'
								}"
							>
								{{ proposal.status || "---" }}
							</span>
						</div>
						<!-- Number of offers -->
						<div>
							<span class="c1 fs3 fw7 lh125"
								>{{ $t("number_of_offers") }}:
								{{ proposal.numberOfOffers }}</span
							>
						</div>
						<!-- Renewal tag -->
						<div class="mt2 mb2" v-if="proposal.renewalCount">
							<j-tag :label="$t('renewal')" variant="neutral" />
							<span class="fs3 c6 ml2" v-if="proposal.renewedFromNumber">
								{{ $t("renewed_from") }}
								<span class="fw7">{{ proposal.renewedFromNumber }}</span>
							</span>
						</div>
					</div>
				</div>

				<!-- Actions -->
				<v-popover
					class="xs-5 flex xs-right"
					placement="bottom-end"
					popoverClass="xs-12 r2"
					popoverArrowClass="xs-0"
					popoverInnerClass="xs-12 r2 bg5 b1 bc7"
				>
					<j-button
						type="bordered"
						size="xsmall"
						:isLoading="isActionLoading === proposal.id"
					>
						{{ $t("actions") }}
						<crm-icon name="down" color="c2" size="small" />
					</j-button>
					<template slot="popover">
						<div
							class="xs-12 bb1 bc7 p3 fw7 cu2 c1"
							@click="createPolicies({ proposalId: proposal.id })"
							v-close-popover
						>
							{{ $t("create_policy") }}
						</div>
						<div
							class="xs-12 bb1 bc7 p3 fw7 cu2 c1"
							@click="renewProposal({ proposalId: proposal.id })"
							v-close-popover
						>
							{{ $t("renew_this_proposal") }}
						</div>
						<div
							class="xs-12 bb1 bc7 p3 fw7 cu2 c1"
							@click="duplicateProposal({ proposalId: proposal.id })"
							v-close-popover
						>
							{{ $t("duplicate_this_proposal") }}
						</div>
						<div
							class="xs-12 p3 fw7 cError cu2"
							@click="removeProposal({ proposalId: proposal.id })"
							v-close-popover
						>
							{{ $t("delete_proposal") }}
						</div>
					</template>
				</v-popover>
			</div>
		</j-card>

		<!-- no proposals -->
		<j-card v-if="!proposals.length" class="mb4 bg5">
			<div class="xs-12 flex wrap xs-middle p4 c6">
				{{ $t("errors.no_proposals") }}
			</div>
		</j-card>

		<!-- Create Modal -->
		<core-modal name="addProposal" :headline="$t('add_proposal')">
			<div class="p4">
				<div class="mb6">
					<div class="xs-12">
						<div class="block pb2">
							<label for="proposalName">Proposal name</label>
						</div>
						<j-base-input-text v-model="form.proposalName" id="proposalName" />
					</div>
				</div>
				<j-button
					class="mt6"
					@click="addProposal({ name: form.proposalName })"
					:isLoading="isLoading"
					>Add</j-button
				>
			</div>
		</core-modal>
	</div>
</template>

<script>
import JCard from "@/components/JCard"
import JButton from "@/components/JButton"
import CrmIcon from "@/components/CrmIcon"
import JTag from "@/components/JTag/"
import CoreModal from "@/modules/core/components/CoreModal"
import JBaseInputText from "@/components/JBaseInputText"
import dayjs from "dayjs"
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"

export default {
	components: {
		JCard,
		JButton,
		CrmIcon,
		JTag,
		CoreModal,
		JBaseInputText
	},

	data() {
		return {
			// For renewals
			proposal: {},
			isActionLoading: false,

			isLoading: false,
			form: { proposalName: "" }
		}
	},

	computed: {
		proposals() {
			const proposals = this.$store?.state?.caseManagement?.proposals || []

			return proposals.map(item => ({
				id: item.id,
				name: item.name,
				proposalNumber: item.proposalNumber,
				status: item.status,
				createdAt: item.createdAt
					? dayjs(item.createdAt).format(this.$store.state.settings.dateFormat)
					: "",
				renewedFromId: item.renewalHistory?.renewedFrom?.id,
				renewalCount: item?.renewalHistory?.renewalCount,
				renewedFromNumber: item.renewalHistory?.renewedFrom?.proposalNumber,
				numberOfOffers: item?.basket?.length || 0
			}))
		}
	},

	methods: {
		async removeProposal({ proposalId }) {
			const query = gql`
				mutation removeProposal($caseId: String!, $proposalId: String!) {
					removeProposal(caseId: $caseId, proposalId: $proposalId) {
						status
						errors
					}
				}
			`

			this.isActionLoading = proposalId
			const res = await api({
				query,
				variables: {
					caseId: this.$store.state.caseManagement.caseId,
					proposalId
				}
			})
			this.isActionLoading = ""

			// Error handling
			if (res?.data?.removeProposal?.status !== "success") {
				this.$store.dispatch(
					"showError",
					res?.data?.removeProposal?.errors?.[0] || [this.$t("errors.general")]
				)
				return
			}

			if (this.$store.state.caseManagement.proposalId === proposalId) {
				this.$store.commit("caseManagement/SET_PROPOSAL_ID", null)
				this.$store.commit("caseManagement/SET_OFFER_ID", null)
			}

			this.refetchStoreProposals()
		},
		async duplicateProposal({ proposalId }) {
			const query = gql`
				mutation copyProposal($caseId: String!, $input: copyProposalInput!) {
					copyProposal(caseId: $caseId, input: $input) {
						status
						errors
					}
				}
			`
			this.isActionLoading = proposalId
			const res = await api({
				query,
				variables: {
					caseId: this.$store.state.caseManagement.caseId,
					input: {
						copiedFromId: proposalId
					}
				}
			})
			this.isActionLoading = ""

			// Check errors
			if (res?.data?.copyProposal?.status !== "success") {
				this.$store.dispatch(
					"showError",
					res?.data?.copyProposal?.errors?.[0] || this.$t("errors.general")
				)
				return
			}

			// Succes
			this.refetchStoreProposals()
		},
		async renewProposal({ proposalId }) {
			const query = gql`
				mutation renewProposal($caseId: String!, $input: renewProposalInput!) {
					renewProposal(caseId: $caseId, input: $input) {
						status
						errors
					}
				}
			`
			this.isActionLoading = proposalId
			const res = await api({
				query,
				variables: {
					caseId: this.$store.state.caseManagement.caseId,
					input: {
						renewedFromId: proposalId
					}
				}
			})
			this.isActionLoading = ""

			// Check errors
			if (res?.data?.renewProposal?.status !== "success") {
				this.$store.dispatch(
					"showError",
					res?.data?.renewProposal?.errors?.[0] || this.$t("errors.general")
				)
				return
			}

			// Succes
			this.refetchStoreProposals()
		},

		async createPolicies({ proposalId }) {
			const query = gql`
				mutation generatePoliciesFromProposal(
					$caseId: String!
					$proposalId: String!
				) {
					generatePoliciesFromProposal(
						caseId: $caseId
						proposalId: $proposalId
					) {
						status
						errors
						createdStatus {
							id
							ids
						}
					}
				}
			`

			this.isActionLoading = proposalId
			const res = await api({
				query,
				variables: {
					caseId: this.$store.state.caseManagement.caseId,
					proposalId
				}
			})
			this.isActionLoading = ""

			// Errors
			if (res?.data?.generatePoliciesFromProposal?.status !== "success") {
				this.$store.dispatch(
					"showError",
					res?.data?.generatePoliciesFromProposal?.errors?.[0] ||
						this.$t("errors.general")
				)
				return
			}

			// Show success message
			this.$store.dispatch("addMessage", {
				delay: "1000",
				message: { type: "success", text: this.$t("successfully_created") }
			})

			this.$emit("proposal:policies_created", {
				proposalId,
				policiesIds: res?.data?.generatePoliciesFromProposal?.createdStatus.ids
			})
			this.refetchStoreProposals()
		},

		async addProposal(input = {}) {
			const query = gql`
				mutation addProposal($caseId: String!, $input: addProposalInput!) {
					addProposal(caseId: $caseId, input: $input) {
						status
						errors
						createdStatus {
							id
						}
					}
				}
			`

			this.isLoading = true

			const res = await api({
				query,
				variables: {
					caseId: this.$store.state.caseManagement.caseId,
					input
				}
			})

			this.isLoading = false

			// Errors
			if (res?.data?.addProposal?.status !== "success") {
				this.$store.dispatch(
					"showError",
					res?.data?.addProposal?.errors?.[0] || this.$t("errors.general")
				)
				return
			}

			this.$modal.hide("addProposal")
			this.refetchStoreProposals()
		},

		async refetchStoreProposals() {
			return this.$store.dispatch("caseManagement/fetchProposals", {
				caseId: this.$store.state.caseManagement.caseId
			})
		}
	}
}
</script>
